import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WrappedFormUtils } from 'antd/lib/form/Form';

import { Button, Files, RichText, Select } from '~/ui/components';
import RadioSelector from '~/ui/components/RadioSelector/RadioSelector';
import { useAwaitControl } from 'react-redux-await-control';
import {
  createTicketFiles,
  createTicketSupportService,
  getServiceChannels,
  getTicketDetails,
  getTicketResolutionReasons,
  getTicketResolvingAreas,
  getTicketStatus,
  getTicketSupportService,
  getTicketTeams,
} from '~/store/tickets/actions';
import { filteredStatus } from '~/common/tickets';
import { getLoggedUser } from '~/store/user/actions';
import { createSelectValues } from '~/common/selectValues';
import { useDrawer } from '~/hooks/useDrawer';
import { message } from 'antd';
import { StyledSelect } from '../../components/Actions/Actions.styles';
import { StyledForm, Wrapper } from './Services.styles';
import { AWAITING_RESOLUTION, buildSupportServicePayload } from './supportServicePayload';

type ServicesProps = {
  id: string;
};

function Services({ id }: ServicesProps) {
  const form = useRef<WrappedFormUtils>();
  const { closeDrawer, setLoading } = useDrawer();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [descriptionOverLimit, setDescriptionOverLimit] = useState(false);

  const getLoggedUserControl = useAwaitControl(getLoggedUser);
  const getTicketTeamsControl = useAwaitControl(getTicketTeams);
  const getServiceChannelsControl = useAwaitControl(getServiceChannels);
  const getTicketStatusControl = useAwaitControl(getTicketStatus);
  const getTicketDetailsControl = useAwaitControl(getTicketDetails);
  const getTicketResolutionReasonsControl = useAwaitControl(getTicketResolutionReasons);
  const createTicketSupportServiceControl = useAwaitControl(createTicketSupportService);
  const getTicketSupportServiceControl = useAwaitControl(getTicketSupportService);
  const getTicketAreasControl = useAwaitControl(getTicketResolvingAreas);

  const isLoadingTeams = getTicketTeamsControl.isRunning();
  const channelsLoading = getServiceChannelsControl.isRunning();
  const statusLoading = getTicketStatusControl.isRunning();
  const resolutionReasonsLoading = getTicketResolutionReasonsControl.isRunning();
  const isCreatingService = createTicketSupportServiceControl.isRunning(id);
  const ticketDetailsLoading = getTicketDetailsControl.isRunning(id);
  const areasLoading = getTicketAreasControl.isRunning();

  const loggedUser = getLoggedUserControl.result();
  const ticketTeams = getTicketTeamsControl.result();
  const channels = getServiceChannelsControl.result();
  const status = getTicketStatusControl.result();
  const ticketDetail = getTicketDetailsControl.result(id);
  const resolutionReasons = getTicketResolutionReasonsControl.result();
  const areas = getTicketAreasControl.result();

  const createTicketFilesControl = useAwaitControl(createTicketFiles);
  const createTicketFilesLoading = createTicketFilesControl.isRunning();
  const createTicketFilesSuccess = createTicketFilesControl.isSuccessful();
  const createTicketFilesFailure = createTicketFilesControl.hasFailure();

  const createServiceSuccessfully = createTicketSupportServiceControl.isSuccessful(id);

  const loading = useMemo(
    () => isCreatingService || ticketDetailsLoading || createTicketFilesLoading,
    [isCreatingService, ticketDetailsLoading, createTicketFilesLoading],
  );

  const filteredResolutionReasons = useMemo(() => {
    const filteredItems = resolutionReasons?.filter(
      (reason) => reason.journeys?.includes(ticketDetail?.journeyId) && reason.status?.includes(selectedStatus),
    );
    return filteredItems || [];
  }, [resolutionReasons, ticketDetail, selectedStatus]);

  const updatedStatus = filteredStatus(status, 'CREATE_TICKET')?.map((option) => ({
    label: option.name,
    value: option.id,
  }));

  const handleChangeStatus = useCallback((value) => {
    setSelectedStatus(value);
    form.current.setFieldsValue({ justificationResolutionId: null });
  }, []);

  const handleSubmit = useCallback(
    ({ files, ...values }) => {
      // a master trouxe o buildSupportServicePayload, que já resolve o select
      // hierárquico (folha do array — QA TC18) e o resolvingAreaId por status
      const data = buildSupportServicePayload(values, loggedUser, selectedStatus);

      if (files) {
        createTicketFilesControl.start({
          ticketId: id,
          files,
          createdBy: loggedUser?.name,
          createdById: loggedUser?.id,
          personId: ticketDetail?.personId,
        });
      }

      createTicketSupportServiceControl.start({ id, data }, { actionId: id });
    },
    [loggedUser, id, ticketDetail, selectedStatus],
  );

  const inputs = useMemo(
    () => [
      {
        id: 'serviceChannelId',
        label: 'Canal',
        input: (
          <StyledSelect
            popupPlacement="left"
            options={channels}
            loading={channelsLoading}
            fieldNames={{ label: 'name', value: 'id' }}
          />
        ),
        options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
      },
      {
        id: 'teamId',
        label: 'Time',
        input: (
          <StyledSelect
            popupPlacement="left"
            options={ticketTeams}
            loading={isLoadingTeams}
            fieldNames={{ label: 'name', value: 'id' }}
          />
        ),
        options: { rules: [{ required: true, message: 'Este campo é obrigatório!' }] },
      },
      {
        id: 'contactOrigin',
        label: 'Origem do contrato',
        initialValue: ticketDetail?.contactOrigin,
        input: (
          <RadioSelector
            orientation="row"
            options={[
              { label: 'Ativo', value: 'ACTIVE' },
              { label: 'Receptivo', value: 'RECEPTIVE' },
            ]}
          />
        ),
        options: {
          rules: [{ required: true, message: 'Este campo é obrigatório' }],
        },
      },
      {
        id: 'ticketStatusId',
        label: 'Status da resolução',
        initialValue: ticketDetail?.ticketStatusId,
        input: <RadioSelector onChange={handleChangeStatus} options={updatedStatus} loading={statusLoading} />,
        options: {
          rules: [{ required: true, message: 'Este campo é obrigatório' }],
        },
      },
      {
        id: 'resolvingAreaId',
        label: 'Área resolvedora',
        initialValue: createSelectValues(areas, ticketDetail?.resolvingAreaId),
        input: (
          <Select
            options={areas}
            placeholder="Selecione a área resolvedora"
            loading={areasLoading}
            fieldNames={{ label: 'name', value: 'id' }}
            testId="area"
            popupPlacement="left"
            resize={selectedStatus === AWAITING_RESOLUTION}
          />
        ),
        options: {
          rules: [{ required: selectedStatus === AWAITING_RESOLUTION, message: 'Selecione a área resolvedora' }],
        },
        hidden: selectedStatus !== AWAITING_RESOLUTION,
      },
      {
        id: 'justificationResolutionId',
        label: 'Justificativa da resolução',
        initialValue: createSelectValues(resolutionReasons, ticketDetail?.justificationResolutionId),
        input: (
          <StyledSelect
            options={filteredResolutionReasons}
            loading={resolutionReasonsLoading}
            disabled={!filteredResolutionReasons?.length}
            fieldNames={{ label: 'justification', value: 'id' }}
            testId="new-service-justification-resolution"
            popupPlacement="left"
          />
        ),
        options: {
          rules: [{ required: true, message: 'Este campo é obrigatório' }],
        },
      },
      {
        id: 'description',
        label: 'Descrição',
        input: <RichText placeholder="Digite aqui a descrição do ticket" maxLength={700} />,
        options: {
          rules: [
            { required: true, message: 'Este campo é obrigatório' },
            {
              validator: (rule, value, callback) => {
                const text = (value || '').replace(/<[^>]*>/g, '');
                if (text.length >= 700) {
                  callback(`Limite de 700 caracteres atingido. Reduza o texto para continuar.`);
                } else {
                  callback();
                }
              },
            },
          ],
        },
      },
      {
        id: 'files',
        skipArrayFormat: true,
        input: <Files />,
      },
    ],
    [
      ticketTeams,
      isLoadingTeams,
      channels,
      channelsLoading,
      filteredResolutionReasons,
      updatedStatus,
      resolutionReasonsLoading,
      ticketDetail,
      areas,
      areasLoading,
    ],
  );

  useEffect(() => {
    const files = form.current.getFieldsValue(['files']);

    if (files?.files) {
      if (createTicketFilesSuccess || createTicketFilesFailure) {
        closeDrawer('new-service');
        message.success('Atendimento criado com sucesso!');
        createTicketSupportServiceControl.clear({ actionId: id });
        getTicketSupportServiceControl.start({ id }, { actionId: id });
      }
    }
  }, [createTicketFilesSuccess, createTicketFilesFailure]);

  useEffect(() => {
    const files = form.current.getFieldsValue(['files']);

    if (createServiceSuccessfully && !files?.files) {
      closeDrawer('new-service');
      message.success('Atendimento criado com sucesso!');
      createTicketSupportServiceControl.clear({ actionId: id });
      getTicketSupportServiceControl.start({ id }, { actionId: id });
    }
  }, [createServiceSuccessfully]);

  useEffect(() => {
    if (!ticketDetail) {
      getTicketDetailsControl.start({ id }, { actionId: id });
    }
  }, [ticketDetail]);

  useEffect(() => {
    if (!status?.length) getTicketStatusControl.start();
    if (!channels.length) getServiceChannelsControl.start();
    if (!resolutionReasons?.length) getTicketResolutionReasonsControl.start();
    if (!ticketTeams.length) getTicketTeamsControl.start();
    if (!areas?.length) getTicketAreasControl.start();
  }, [status?.length, channels?.length, ticketTeams?.length, resolutionReasons?.length, areas?.length]);

  useEffect(() => {
    if (ticketDetail?.ticketStatusId) {
      setSelectedStatus(ticketDetail?.ticketStatusId);
    }

    return () => {
      createTicketFilesControl.clear();
      createTicketSupportServiceControl.clear();
    };
  }, []);

  useEffect(() => {
    setLoading('new-service', loading);
  }, [loading]);

  return (
    <StyledForm
      ref={form}
      name="reset-form"
      onSubmit={handleSubmit}
      inputs={inputs}
      onValuesChange={(changedValues) => {
        if ('description' in changedValues) {
          const text = (changedValues.description || '').replace(/<[^>]*>/g, '');
          setDescriptionOverLimit(text.length >= 700);
        }
      }}
    >
      <Wrapper>
        <Button
          rounded
          type="submit"
          disabled={isCreatingService || createTicketFilesLoading || descriptionOverLimit}
          loading={isCreatingService || createTicketFilesLoading}
          data-testid="ticket-form-button"
        >
          Finalizar
        </Button>
      </Wrapper>
    </StyledForm>
  );
}

export default Services;
